<script lang="ts" setup>
import { createPopper, type Instance as PopperInstance } from '@popperjs/core'
import { SentenceType } from 'types/types'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { VOICE_MODE_READING, VOICE_MODE_SINGING } from '@/constants/practiceConstants'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import {
  hasProcessingError,
  hasRecordingError,
  hasSegmentMismatch,
  isProcessing,
  isRead,
  isSung,
  nAudioSegments,
  nSentenceSegments,
} from '@/util/RecordingHelpers'

// {{{ props
interface Props {
  sentenceIndex: number
  sentence: SentenceType
  recordings: SentenceRecordingMetadataWithPitchedAudios[]
  playingSentenceRecordingId: string | null
  downloadingSentenceRecordingId: string | null
  playing: boolean
  paused: boolean
  visible: boolean
  anchorId: string | null
}

const props = defineProps<Props>()
// }}}

// {{{ emits
interface Emits {
  (e: 'close'): void
  (e: 'play', metadata: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'pause'): void
  (e: 'resume'): void
  (e: 'edit', metadata: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'delete', metadata: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'download', metadata: SentenceRecordingMetadataWithPitchedAudios): void
  (e: 'quickRecord', mode: 'reading' | 'singing'): void
}

const emit = defineEmits<Emits>()
// }}}

// {{{ popper management
const popoverEl = ref<HTMLElement | null>(null)
let popperInstance: PopperInstance | null = null

function createPopperInstance() {
  if (!props.anchorId || !popoverEl.value) {
    return
  }
  const anchorEl = document.getElementById(props.anchorId)
  if (!anchorEl) {
    return
  }
  popperInstance = createPopper(anchorEl, popoverEl.value, {
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 16 } },
    ],
    placement: 'bottom',
  })
}

function destroyPopper() {
  if (popperInstance) {
    popperInstance.destroy()
    popperInstance = null
  }
}

onMounted(() => {
  if (props.visible) {
    createPopperInstance()
  }
})

onBeforeUnmount(() => {
  destroyPopper()
})

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      createPopperInstance()
    } else {
      destroyPopper()
    }
  },
)

watch(
  () => props.anchorId,
  () => {
    destroyPopper()
    if (props.visible) {
      createPopperInstance()
    }
  },
)
// }}}

// {{{ recording categorization
const readRecordings = computed(() => props.recordings.filter(isRead))
const sungRecordings = computed(() => props.recordings.filter(isSung))
const hasReading = computed(() => readRecordings.value.length > 0)
const hasSinging = computed(() => sungRecordings.value.length > 0)
// }}}

// {{{ helpers
function isCurrentlyPlaying(item: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return props.playing && !!item.sentenceRecording && props.playingSentenceRecordingId === item.sentenceRecording.id
}

function isCurrentlyPaused(item: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return props.paused && !!item.sentenceRecording && props.playingSentenceRecordingId === item.sentenceRecording.id
}

function isCurrentlyDownloading(item: SentenceRecordingMetadataWithPitchedAudios): boolean {
  return !!item.sentenceRecording && props.downloadingSentenceRecordingId === item.sentenceRecording.id
}

function handlePlayPause(item: SentenceRecordingMetadataWithPitchedAudios) {
  if (isCurrentlyPlaying(item)) {
    emit('pause')
  } else if (isCurrentlyPaused(item)) {
    emit('resume')
  } else {
    emit('play', item)
  }
}

function handleRestart(item: SentenceRecordingMetadataWithPitchedAudios) {
  // Restart always plays from the beginning — reuse the fresh-play 'play' event.
  emit('play', item)
}

function segmentStatusLabel(item: SentenceRecordingMetadataWithPitchedAudios): { icon: string; label: string } {
  if (isProcessing(item)) {
    return { icon: 'mdi-loading mdi-spin', label: 'Processing...' }
  }
  if (hasProcessingError(item)) {
    const message = item.sentenceRecording?.processingMessage
    return { icon: 'mdi-alert-circle', label: message || 'Error' }
  }
  if (hasSegmentMismatch(item)) {
    return { icon: 'mdi-alert-circle', label: `${nAudioSegments(item)}/${nSentenceSegments(item)} phrases` }
  }

  const nSentence = nSentenceSegments(item)
  const nAudio = nAudioSegments(item)

  // Custom (non-standard) segmentation: segmented_sentence exists
  if (nSentence > 1) {
    return { icon: 'mdi-check-circle', label: `${nSentence} phrases` }
  }
  if (nSentence === 1) {
    return { icon: 'mdi-check-circle', label: '1 sentence' }
  }

  // Standard segmentation: no segmented_sentence (nSentence === -1).
  // Infer recording type from audio segment count:
  //   nAudio > 1 → word-level recording (one audio file per word)
  //   nAudio <= 1 → sentence-level recording (no split stored)
  if (nAudio > 1) {
    return { icon: 'mdi-check-circle', label: `${nAudio} words` }
  }
  return { icon: 'mdi-check-circle', label: '1 sentence' }
}

function segmentStatusColor(item: SentenceRecordingMetadataWithPitchedAudios): string {
  if (isProcessing(item)) {
    return 'grey'
  }
  return hasRecordingError(item) ? 'error' : 'success'
}

function recordingLabel(item: SentenceRecordingMetadataWithPitchedAudios): string {
  const sr = item.sentenceRecording
  if (!sr) {
    return ''
  }
  if (isRead(item)) {
    return 'Reading'
  }
  const melodyName = sr.torahMelody?.description || sr.haftarahMelody?.description || sr.sentenceGroupMelody?.description || ''
  return melodyName ? `Singing — ${melodyName}` : 'Singing'
}
// }}}

// {{{ close on click outside
function handleClickOutside(event: MouseEvent) {
  if (popoverEl.value && !popoverEl.value.contains(event.target as Node)) {
    // Check if click was on the sentence itself (which handles its own toggle)
    const anchorEl = props.anchorId ? document.getElementById(props.anchorId) : null
    if (anchorEl && anchorEl.contains(event.target as Node)) {
      return
    }
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true)
})
// }}}
</script>

<template>
  <div v-show="visible" ref="popoverEl" class="sentence-popover">
    <!-- Header -->
    <div class="popover-header">
      <span class="text-subtitle-1 font-weight-bold">Sentence {{ sentenceIndex + 1 }}</span>
      <v-btn density="compact" icon size="small" variant="text" @click="emit('close')">
        <v-icon size="small">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider />

    <!-- Recordings list -->
    <div v-if="recordings.length" class="popover-recordings">
      <div v-for="item in recordings" :key="item.sentenceRecording?.id ?? ''" class="recording-row">
        <!-- Play/Pause (hidden while processing) -->
        <v-btn v-if="!isProcessing(item)" density="comfortable" icon size="small" variant="text" @click="handlePlayPause(item)">
          <v-icon>{{ isCurrentlyPlaying(item) ? 'mdi-pause' : 'mdi-play' }}</v-icon>
        </v-btn>

        <!-- Restart from beginning -->
        <v-btn v-if="!isProcessing(item)" density="comfortable" icon size="small" variant="text" @click="handleRestart(item)">
          <v-icon>mdi-restart</v-icon>
        </v-btn>

        <!-- Label -->
        <span class="recording-label text-body-1">{{ recordingLabel(item) }}</span>

        <!-- Segment status -->
        <span v-if="segmentStatusLabel(item).label" class="segment-status">
          <v-icon :color="segmentStatusColor(item)" size="small">{{ segmentStatusLabel(item).icon }}</v-icon>
          <span class="text-body-2 text-medium-emphasis">{{ segmentStatusLabel(item).label }}</span>
        </span>

        <!-- Actions -->
        <v-btn density="comfortable" icon size="small" variant="text" @click="emit('edit', item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn density="comfortable" icon size="small" variant="text" @click="emit('download', item)">
          <v-progress-circular v-if="isCurrentlyDownloading(item)" indeterminate size="20" width="2" />
          <v-icon v-else>mdi-download</v-icon>
        </v-btn>
        <v-btn color="error" density="comfortable" icon size="small" variant="text" @click="emit('delete', item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>
    </div>
    <div v-else class="popover-empty">
      <span class="text-body-1 text-medium-emphasis">No recordings yet</span>
    </div>

    <v-divider />

    <!-- Quick-record buttons -->
    <div class="popover-actions">
      <v-btn
        :color="hasReading ? 'default' : 'primary'"
        prepend-icon="mdi-microphone"
        :variant="hasReading ? 'outlined' : 'flat'"
        @click="emit('quickRecord', VOICE_MODE_READING)"
      >
        Record Reading
      </v-btn>
      <v-btn
        :color="hasSinging ? 'default' : 'primary'"
        prepend-icon="mdi-microphone"
        :variant="hasSinging ? 'outlined' : 'flat'"
        @click="emit('quickRecord', VOICE_MODE_SINGING)"
      >
        Record Singing
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.sentence-popover {
  position: absolute;
  z-index: 100;
  background: rgb(var(--v-theme-surface));
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  box-shadow: 0 4px 16px rgba(var(--v-theme-on-surface), 0.15);
  min-width: 360px;
  max-width: 480px;
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.popover-recordings {
  padding: 6px 12px;
}

.recording-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border-radius: 6px;
  transition: background-color 0.15s;
  overflow: hidden;
}

.recording-row:hover {
  background-color: #f5f5f5;
}

.recording-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
  min-width: 60px;
}

.segment-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}

.segment-status .text-body-2 {
  overflow: hidden;
  text-overflow: ellipsis;
}

.popover-empty {
  padding: 16px 20px;
  text-align: center;
}

.popover-actions {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  justify-content: center;
}
</style>
